import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './BlogDetail.css';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import { blogAPI } from '../../api';

const BlogDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [relatedBlogs, setRelatedBlogs] = useState([]);

    useEffect(() => {
        if (slug) {
            fetchBlogBySlug();
        }
    }, [slug]);

    const fetchBlogBySlug = async () => {
        try {
            setLoading(true);
            setError(null);
            
            console.log('Fetching blog with slug:', slug);
            
            // Essayer d'abord avec le slug
            let response = await blogAPI.getBySlug(slug);
            
            console.log('Blog detail response:', response);
            
            // Si pas trouvé par slug, essayer par ID (au cas où)
            if (!response.success && !isNaN(slug)) {
                console.log('Trying by ID...');
                response = await blogAPI.getById(slug);
            }
            
            if (response.success && response.data) {
                setBlog(response.data);
                
                // Récupérer d'autres blogs pour les articles similaires
                await fetchRelatedBlogs(response.data);
            } else {
                setError('Blog non trouvé');
            }
        } catch (error) {
            console.error('Erreur lors du chargement du blog:', error);
            setError(error.message || 'Erreur lors du chargement du blog');
        } finally {
            setLoading(false);
        }
    };

    const fetchRelatedBlogs = async (currentBlog) => {
        try {
            const response = await blogAPI.getAll(1);
            
            if (response.success && response.data && response.data.data) {
                // Prendre les blogs publiés, différents du courant
                const related = response.data.data
                    .filter(b => b.id !== currentBlog.id && b.is_published === true)
                    .slice(0, 2);
                
                setRelatedBlogs(related);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des articles similaires:', error);
            // Ne pas bloquer l'affichage principal
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return 'Date non disponible';
        }
    };

    const getImageUrl = (blog) => {
        return blog.image_url || blog.image || '/default-blog.jpg';
    };

    const handleImageError = (e, title) => {
        console.warn('Failed to load blog image:', title);
        e.target.onerror = null;
        
        // SVG de fallback
        const svgFallback = `
            <svg xmlns="http://www.w3.org/2000/svg" width="800" height="400" viewBox="0 0 800 400">
                <rect width="800" height="400" fill="#f8f9fa"/>
                <text x="400" y="180" font-family="Arial" font-size="24" text-anchor="middle" fill="#6c757d">
                    ${blog?.category || 'Blog ETEF'}
                </text>
                <text x="400" y="220" font-family="Arial" font-size="18" text-anchor="middle" fill="#adb5bd">
                    ${title?.substring(0, 50) || 'Article'}
                </text>
            </svg>
        `;
        
        e.target.src = `data:image/svg+xml;utf8,${encodeURIComponent(svgFallback)}`;
    };

    const handleBack = () => {
        navigate('/blog');
    };

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: blog.title,
                text: blog.excerpt || blog.content?.substring(0, 150),
                url: window.location.href,
            }).catch(console.error);
        } else {
            navigator.clipboard.writeText(window.location.href);
            alert('Lien copié dans le presse-papier !');
        }
    };

    const handleSocialShare = (platform) => {
        const shareUrl = window.location.href;
        const title = blog?.title || '';
        
        let shareLink = '';
        switch (platform) {
            case 'facebook':
                shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
                break;
            case 'twitter':
                shareLink = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`;
                break;
            case 'linkedin':
                shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
                break;
            default:
                return;
        }
        
        window.open(shareLink, '_blank', 'width=600,height=400');
    };

    if (loading) {
        return (
            <div className="blog-detail-container-bigcontain">
                <Header />
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Chargement du blog...</p>
                </div>
                <Footer />
            </div>
        );
    }

    if (error || !blog) {
        return (
            <div className="blog-detail-container-bigcontain">
                <Header />
                <div className="error-container">
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="#dc3545">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    <h2>Blog non trouvé</h2>
                    <p>{error || "Le blog que vous cherchez n'existe pas ou a été supprimé."}</p>
                    <button onClick={handleBack} className="back-btn">
                        ← Retour aux blogs
                    </button>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="blog-detail-container-bigcontain">
            <Header />
            <div className="blog-detail-container">
                
                {/* Header avec bouton retour */}
                <div className="blog-header">
                    <button onClick={handleBack} className="back-button">
                        <svg className="back-icon" viewBox="0 0 24 24" width="24" height="24">
                            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
                        </svg>
                        Retour aux articles
                    </button>
                    
                    <div className="header-actions">
                        <button onClick={handleShare} className="share-button">
                            <svg className="share-icon" viewBox="0 0 24 24" width="20" height="20">
                                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
                            </svg>
                            Partager
                        </button>
                    </div>
                </div>

                {/* Image principale */}
                <div className="blog-hero">
                    <div className="hero-image-container">
                        <img 
                            src={getImageUrl(blog)} 
                            alt={blog.title}
                            className="hero-image"
                            onError={(e) => handleImageError(e, blog.title)}
                        />
                        <div className="image-overlay"></div>
                    </div>
                </div>

                {/* Contenu principal */}
                <article className="blog-content">
                    <header className="content-header">
                        <div className="blog-categories">
                            <span className="blog-category">{blog.category || 'Non catégorisé'}</span>
                        </div>
                        
                        <h1 className="blog-title">{blog.title}</h1>
                        
                        <div className="blog-meta">
                            <div className="meta-item">
                                <svg className="meta-icon" viewBox="0 0 24 24" width="20" height="20">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                                </svg>
                                <span className="meta-text">
                                    Publié le {formatDate(blog.published_date || blog.created_at)}
                                </span>
                            </div>
                            
                            {blog.author && (
                                <div className="meta-item">
                                    <svg className="meta-icon" viewBox="0 0 24 24" width="20" height="20">
                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                    </svg>
                                    <span className="meta-text">
                                        Par {blog.author}
                                    </span>
                                </div>
                            )}
                        </div>
                    </header>

                    {/* Contenu du blog */}
                    <div className="blog-body">
                        <div 
                            className="blog-text"
                            dangerouslySetInnerHTML={{ __html: blog.content || '<p>Contenu non disponible</p>' }}
                        />
                    </div>

                    {/* Tags */}
                    <div className="blog-tags">
                        <span className="tags-label">Mots-clés:</span>
                        <div className="tags-list">
                            <span className="tag">Études en France</span>
                            {blog.category && <span className="tag">{blog.category}</span>}
                            <span className="tag">ETEF</span>
                            {blog.author && <span className="tag">{blog.author}</span>}
                        </div>
                    </div>

                    {/* Actions sociales */}
                    <div className="social-share">
                        <h3>Partager cet article</h3>
                        <div className="share-buttons">
                            <button 
                                className="social-btn facebook"
                                onClick={() => handleSocialShare('facebook')}
                            >
                                Facebook
                            </button>
                            <button 
                                className="social-btn twitter"
                                onClick={() => handleSocialShare('twitter')}
                            >
                                Twitter
                            </button>
                            <button 
                                className="social-btn linkedin"
                                onClick={() => handleSocialShare('linkedin')}
                            >
                                LinkedIn
                            </button>
                        </div>
                    </div>
                </article>

                {/* Blogs similaires */}
                {relatedBlogs.length > 0 && (
                    <section className="related-blogs">
                        <h2 className="related-title">Articles similaires</h2>
                        <p className="related-subtitle">Découvrez d'autres articles qui pourraient vous intéresser</p>
                        
                        <div className="related-grid">
                            {relatedBlogs.map(relatedBlog => (
                                <article key={relatedBlog.id} className="related-card">
                                    <div 
                                        className="related-image"
                                        onClick={() => navigate(`/blog/${relatedBlog.slug || relatedBlog.id}`)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <img 
                                            src={getImageUrl(relatedBlog)} 
                                            alt={relatedBlog.title}
                                            loading="lazy"
                                            onError={(e) => handleImageError(e, relatedBlog.title)}
                                        />
                                    </div>
                                    <div className="related-content">
                                        <h3 className="related-card-title">{relatedBlog.title}</h3>
                                        <p className="related-excerpt">
                                            {relatedBlog.excerpt || relatedBlog.content?.substring(0, 100) + '...'}
                                        </p>
                                        <a 
                                            href={`/blog/${relatedBlog.slug || relatedBlog.id}`} 
                                            className="related-link"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(`/blog/${relatedBlog.slug || relatedBlog.id}`);
                                            }}
                                        >
                                            Lire la suite →
                                        </a>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </section>
                )}

                {/* Footer avec bouton retour */}
                <footer className="blog-footer">
                    <button onClick={handleBack} className="footer-back-btn">
                        ← Retour à la liste des blogs
                    </button>
                </footer>
            </div>
            <Footer />
        </div>
    );
};

export default BlogDetail;